// Cut list — ported from the prototype's allParts()/corte()/exportCsv().
import { zocaloCm } from './geometry';
import { materialLabel } from './materials';
import { boardThick, parts } from './parts';
import type { ProjectData } from './schema';
import type { MaterialDefinition } from './types';

export interface CutRow {
  pieza: string;
  L: number;
  A: number;
  veta: string;
  cantos: string;
  cant: number;
  mods: number[];
}

export interface CutGroup {
  mat: string;
  matCode: string;
  esp: number;
  rows: CutRow[];
  pieces: number;
  /** m² */
  area: number;
  /** Boards needed (sheet area with 15 % waste). */
  boards: number;
  /** Sheet the material is sold in (mm) and its distributor. */
  sheet: [number, number];
  supplier: string | null;
}

export const BOARD = { L: 2440, A: 1830, areaM2: 4.47, waste: 0.15 } as const;
/** Sheet of a material (mm): the one set in the board library, else the standard 2440 × 1830. */
export const sheetOf = (mat?: Pick<MaterialDefinition, 'sheet'>): [number, number] => (mat?.sheet && mat.sheet[0] >= 300 && mat.sheet[1] >= 300 ? mat.sheet : [BOARD.L, BOARD.A]);

export function allParts(project: ProjectData, materials: Record<string, MaterialDefinition>) {
  return project.mods.flatMap((m) => parts(m, project.mats, materials, project.prefs.junta, zocaloCm(project.prefs.zocalo)).map((p) => ({ mod: m.id, ...p })));
}

/** Panels (planchas) fixed on the walls, as pieces of their board (mm). They belong to no module (mod 0). */
export function wallPanelParts(project: ProjectData, materials: Record<string, MaterialDefinition>) {
  return (project.panels ?? []).map((pn) => {
    const mat = materials[pn.mat];
    const L = Math.round(Math.max(pn.w, pn.h) * 10);
    const A = Math.round(Math.min(pn.w, pn.h) * 10);
    return { mod: 0, pieza: `Plancha muro ${pn.wall}`, cant: 1, L, A, esp: boardThick(mat), mat: materialLabel(mat, pn.mat), matCode: pn.mat, veta: pn.h >= pn.w ? 'Vertical' : 'Horizontal', cantos: '4L' };
  });
}

export function corte(project: ProjectData, materials: Record<string, MaterialDefinition>): CutGroup[] {
  const groups = new Map<string, { mat: string; matCode: string; esp: number; rows: Map<string, CutRow>; pieces: number; area: number }>();
  for (const p of [...allParts(project, materials), ...wallPanelParts(project, materials)]) {
    const key = `${p.mat}|${p.esp}`;
    let g = groups.get(key);
    if (!g) groups.set(key, (g = { mat: p.mat, matCode: p.matCode, esp: p.esp, rows: new Map(), pieces: 0, area: 0 }));
    const rk = `${p.pieza}|${p.L}|${p.A}`;
    let r = g.rows.get(rk);
    if (!r) g.rows.set(rk, (r = { pieza: p.pieza, L: p.L, A: p.A, veta: p.veta, cantos: p.cantos, cant: 0, mods: [] }));
    r.cant += p.cant;
    if (p.mod && !r.mods.includes(p.mod)) r.mods.push(p.mod);
    g.pieces += p.cant;
    g.area += (p.cant * p.L * p.A) / 1e6;
  }
  return [...groups.values()]
    .map((g) => {
      const sheet = sheetOf(materials[g.matCode]);
      return {
        mat: g.mat,
        matCode: g.matCode,
        esp: g.esp,
        rows: [...g.rows.values()],
        pieces: g.pieces,
        area: g.area,
        boards: Math.ceil((g.area * (1 + BOARD.waste)) / ((sheet[0] * sheet[1]) / 1e6)),
        sheet,
        supplier: materials[g.matCode]?.supplier ?? null,
      };
    })
    .sort((a, b) => b.area - a.area);
}

/** CSV exactly like the prototype's "Exportar CSV" (UTF-8 BOM, all fields quoted). */
export function cutlistCsv(groups: CutGroup[]): string {
  const lines: (string | number)[][] = [['Material', 'Espesor (mm)', 'Pieza', 'Módulos', 'Cantidad', 'Largo (mm)', 'Ancho (mm)', 'Veta', 'Cantos']];
  for (const g of groups) for (const r of g.rows) lines.push([g.mat, g.esp, r.pieza, r.mods.join(' '), r.cant, r.L, r.A, r.veta, r.cantos]);
  return `﻿${lines.map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')}`;
}
